"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import PhosphorIcon, {
  type PhosphorIconName,
} from "@/shared/components/common/PhosphorIcon";
import useSessionStorage from "@/shared/hooks/useSessionStorage";
import type { StorePassengersSchema } from "@/shared/schemas/hotel/storePassengersSchema";
import { useHotelStore } from "@/shared/stores/hotel-store/HotelStoreProvider";
import type {
  HotelDetailV2BookingModel,
  HotelDetailV2RoomModel,
} from "../model/hotelDetailV2.model";
import DetailImage from "./DetailImage";
import { getSelectedRoomsCount } from "../utils/bookingNavigation";

interface RoomsSectionProps {
  booking: HotelDetailV2BookingModel;
  city: string;
  hotelImageUrl?: string;
  slug: string;
}

type MealFilter = "all" | "breakfast" | "room-only" | "full-board";
type CancelFilter = "all" | "cancelable" | "noncancelable";
const MAX_SELECTED_ROOM_COUNT = 4;
type MealFilterOption = {
  key: Exclude<MealFilter, "all">;
  label: string;
  matches: (room: HotelDetailV2RoomModel) => boolean;
};

const MEAL_FILTER_OPTIONS: MealFilterOption[] = [
  {
    key: "room-only",
    label: "فقط اتاق",
    matches: (room) => !room.breakfastIncluded,
  },
  {
    key: "breakfast",
    label: "صبحانه",
    matches: (room) => room.source.rate_plans.meal_type_included === "breakfast",
  },
  {
    key: "full-board",
    label: "سه وعده غذایی",
    matches: (room) => room.source.rate_plans.meal_type_included === "full_board",
  },
];

export default function RoomsSection({
  booking,
  hotelImageUrl,
  slug,
}: RoomsSectionProps) {
  const { rooms, setDetailRooms } = useHotelStore((state) => state);
  const [cachedPassengers, setCachedPassengers] =
    useSessionStorage<StorePassengersSchema | null>("HotelcachedPassengers");
  const [hotelInfo, setHotelInfo] = useSessionStorage<string>("hotelInfo");
  const [mealFilter, setMealFilter] = useState<MealFilter>("all");
  const [cancelFilter, setCancelFilter] = useState<CancelFilter>("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filtersStuck, setFiltersStuck] = useState(false);
  const filtersSentinelRef = useRef<HTMLSpanElement>(null);
  const selectedCount = getSelectedRoomsCount(rooms);
  const selectionKey = `${slug}-${booking.checkIn}-${booking.checkOut}-${booking.roomsParam}`;
  const roomsForDisplay = booking.rooms;
  const visibleMealFilterOptions = MEAL_FILTER_OPTIONS.filter((option) =>
    roomsForDisplay.some(option.matches),
  );
  const hasMealFilterOptions = visibleMealFilterOptions.length > 0;
  const hasCancelableRooms = roomsForDisplay.some((room) => room.cancelable);
  const effectiveMealFilter = visibleMealFilterOptions.some(
    (option) => option.key === mealFilter,
  )
    ? mealFilter
    : "all";
  const effectiveCancelFilter = hasCancelableRooms ? cancelFilter : "all";
  const filteredRooms = roomsForDisplay.filter((room) => {
    const activeMealFilter = MEAL_FILTER_OPTIONS.find(
      (option) => option.key === effectiveMealFilter,
    );
    if (activeMealFilter && !activeMealFilter.matches(room)) return false;
    if (effectiveCancelFilter === "cancelable" && !room.cancelable) return false;
    if (effectiveCancelFilter === "noncancelable" && room.cancelable) return false;
    return true;
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDetailRooms([]);
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [
    booking.checkIn,
    booking.checkOut,
    selectionKey,
    setDetailRooms,
  ]);

  useEffect(() => {
    if (cachedPassengers) setCachedPassengers(null);
  }, [cachedPassengers, setCachedPassengers, selectionKey]);

  useEffect(() => {
    if (hotelInfo) setHotelInfo("");
  }, [hotelInfo, setHotelInfo, selectionKey]);

  useEffect(() => {
    const start = () => setIsRefreshing(true);
    window.addEventListener("hotel-detail-v2:rooms-loading", start);
    return () => window.removeEventListener("hotel-detail-v2:rooms-loading", start);
  }, []);

  useEffect(() => {
    if (!isRefreshing) return;
    const timeout = window.setTimeout(() => setIsRefreshing(false), 550);
    return () => window.clearTimeout(timeout);
  }, [isRefreshing, selectionKey]);

  useEffect(() => {
    const sentinel = filtersSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => setFiltersStuck(!entry.isIntersecting),
      { rootMargin: "-48px 0px 0px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const updateRoomCount = (room: HotelDetailV2RoomModel, nextCount: number) => {
    const current = rooms.find(
      (item) =>
        item.id === room.source.id &&
        item.rate_plans.id === room.source.rate_plans.id,
    );
    const currentCount = current?.count || 0;
    const nextTotal = selectedCount - currentCount + nextCount;
    if (
      nextCount < 0 ||
      nextTotal < 0 ||
      nextTotal > MAX_SELECTED_ROOM_COUNT ||
      nextCount > room.inventory
    ) {
      return;
    }

    if (nextCount === 0) {
      setDetailRooms(
        rooms.filter(
          (item) =>
            !(
              item.id === room.source.id &&
              item.rate_plans.id === room.source.rate_plans.id
            ),
        ),
      );
      return;
    }

    if (current) {
      setDetailRooms(
        rooms.map((item) =>
          item.id === room.source.id &&
          item.rate_plans.id === room.source.rate_plans.id
            ? { ...item, count: nextCount }
            : item,
        ),
      );
      return;
    }

    setDetailRooms([...rooms, { ...room.source, count: nextCount }]);
  };

  return (
    <section id="rooms" className="scroll-mt-28 space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[#1a1a2e]">اتاق‌های موجود</h2>
          <p className="mt-1 text-xs text-[#6b7280]">
            {filteredRooms.length.toLocaleString("fa-IR")} نرخ قابل نمایش
          </p>
        </div>
        {selectedCount > 0 ? (
          <span className="text-xs font-bold text-primary">
            {selectedCount.toLocaleString("fa-IR")} اتاق انتخاب‌شده
          </span>
        ) : null}
      </div>

      <span ref={filtersSentinelRef} className="block h-px" aria-hidden="true" />
      <div
        className={`z-20 space-y-2 border border-[#e5e7eb] bg-[#f5f6f8]/95 p-2 backdrop-blur transition-[border-radius,box-shadow] md:sticky md:top-12 md:z-30 md:shadow-[0_10px_24px_rgba(15,23,42,0.08)] ${
          filtersStuck ? "rounded-b-xl rounded-t-none" : "rounded-xl"
        } ${!hasMealFilterOptions && !hasCancelableRooms ? "md:hidden" : ""}`}
      >
        {hasMealFilterOptions || hasCancelableRooms ? (
        <div className="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0">
          {hasMealFilterOptions ? (
            <FilterButton active={effectiveMealFilter === "all"} onClick={() => setMealFilter("all")}>
              همه
            </FilterButton>
          ) : null}
          {visibleMealFilterOptions.map((option) => (
            <FilterButton
              key={option.key}
              active={effectiveMealFilter === option.key}
              onClick={() =>
                setMealFilter(effectiveMealFilter === option.key ? "all" : option.key)
              }
            >
              {option.label}
            </FilterButton>
          ))}
          {hasCancelableRooms ? (
            <FilterButton active={cancelFilter === "cancelable"} onClick={() => setCancelFilter(cancelFilter === "cancelable" ? "all" : "cancelable")}>
              کنسلی رایگان
            </FilterButton>
          ) : null}
        </div>
        ) : null}
      </div>

      {isRefreshing ? (
        <RoomsSkeleton />
      ) : filteredRooms.length > 0 ? (
        filteredRooms.map((room) => (
          <RoomRateCard
            key={room.key}
            hotelImageUrl={hotelImageUrl}
            room={room}
            nights={booking.nights}
            selectedCount={
              rooms.find(
                (item) =>
                  item.id === room.source.id &&
                  item.rate_plans.id === room.source.rate_plans.id,
              )?.count || 0
            }
            onChange={(count) => updateRoomCount(room, count)}
            remainingSelectable={MAX_SELECTED_ROOM_COUNT - selectedCount}
          />
        ))
      ) : (
        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-5 text-sm leading-7 text-[#6b7280]">
          {booking.availabilityMessage ||
            "با فیلترهای انتخاب‌شده اتاقی برای نمایش پیدا نشد."}
        </div>
      )}

      {/* Desktop submit lives in the sidebar and mobile submit lives in MobileBookingBar. */}
    </section>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-2 text-xs transition ${
        active
          ? "border-primary bg-primary/5 font-bold text-primary"
          : "border-[#e5e7eb] bg-white text-[#6b7280] hover:border-primary"
      }`}
    >
      {children}
    </button>
  );
}

export function RoomsSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white"
        >
          <div className="flex flex-col sm:flex-row">
            <div className="h-44 animate-pulse bg-slate-100 sm:w-52 sm:shrink-0" />
            <div className="flex-1 space-y-3 p-4">
              <div className="h-5 w-48 animate-pulse rounded bg-slate-100" />
              <div className="h-4 w-32 animate-pulse rounded bg-slate-100" />
              <div className="flex gap-2">
                <div className="h-7 w-20 animate-pulse rounded-full bg-slate-100" />
                <div className="h-7 w-24 animate-pulse rounded-full bg-slate-100" />
                <div className="h-7 w-16 animate-pulse rounded-full bg-slate-100" />
              </div>
              <div className="h-12 w-full animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RoomRateCard({
  room,
  hotelImageUrl,
  nights,
  selectedCount,
  onChange,
  remainingSelectable,
}: {
  room: HotelDetailV2RoomModel;
  hotelImageUrl?: string;
  nights: number;
  selectedCount: number;
  onChange: (count: number) => void;
  remainingSelectable: number;
}) {
  const [quantity, setQuantity] = useState(Math.max(1, selectedCount || 1));
  const isSelected = selectedCount > 0;
  const maxQuantity = Math.max(
    1,
    Math.min(room.inventory, selectedCount + remainingSelectable),
  );
  const activeQuantity = isSelected ? selectedCount : quantity;
  const displayTotal = room.totalPrice * activeQuantity;
  const displayOriginalTotal = room.originalTotalPrice
    ? room.originalTotalPrice * activeQuantity
    : undefined;
  const imageUrl = room.imageUrl || hotelImageUrl;
  const roomBadges = createRoomBadges(room);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setQuantity(Math.max(1, selectedCount || 1));
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [selectedCount]);

  return (
    <article className="overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white">
      <div className="flex flex-col sm:flex-row">
        <div className="relative min-h-44 bg-slate-100 sm:w-52 sm:shrink-0">
          {imageUrl ? (
            <DetailImage
              src={imageUrl}
              fallbackSrc={hotelImageUrl || imageUrl}
              alt={room.name}
              fill
              quality={74}
              sizes="(max-width: 639px) 100vw, 208px"
              className="object-cover"
            />
          ) : (
            <div className="flex h-full min-h-44 flex-col items-center justify-center gap-2 text-[#9ca3af]">
              <PhosphorIcon name="ImageSquare" size={28} />
              <span className="text-xs font-medium">تصویر هتل موجود نیست</span>
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col p-4">
          <h3 className="mb-2 text-base font-bold text-[#1a1a2e]">{room.name}</h3>
          {/* TODO: Restore room static attributes after real room area, bed type, and view fields are available. */}
          {roomBadges.length > 0 ? (
            <div className="mb-3 flex flex-wrap gap-2">
              {roomBadges.map((badge) => (
                <Badge
                  key={badge.text}
                  tone={badge.tone}
                  icon={badge.icon}
                  text={badge.text}
                />
              ))}
            </div>
          ) : null}
          <div className="mt-auto grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 border-t border-[#f0f0f0] pt-3 md:grid-cols-[minmax(190px,1fr)_auto_auto]">
            <div className="col-span-2 flex min-w-0 flex-col gap-2 text-right md:col-span-1">
              {displayOriginalTotal ? (
                <p className="text-right text-sm font-bold text-[#6b7280] line-through decoration-2 md:text-right">
                  {displayOriginalTotal.toLocaleString("fa-IR")}
                </p>
              ) : null}
              <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2 md:justify-start">
                <div className="min-w-0">
                  <p className="text-xl font-extrabold leading-8 text-[#111827] md:text-2xl">
                    <span className="ml-1 text-sm font-bold text-[#111827]">از</span>
                    {displayTotal.toLocaleString("fa-IR")}
                    <span className="mr-1 text-sm font-bold">تومان</span>
                  </p>
                  <span className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-[#6b7280]">
                    {nights.toLocaleString("fa-IR")} شب،{" "}
                    {room.capacity.toLocaleString("fa-IR")} نفر
                    <PhosphorIcon name="Users" size={15} className="text-[#9ca3af]" />
                  </span>
                </div>
                {room.discountPercent ? (
                  <Badge
                    tone="discount"
                    icon="Tag"
                    text={`٪${room.discountPercent.toLocaleString("fa-IR")} تخفیف`}
                  />
                ) : null}
                <span className="hidden h-5 w-px bg-[#cbd5e1] md:inline-block" />
                {quantity > 1 ? (
                  <span className="text-xs font-medium text-[#9ca3af]">
                    {activeQuantity.toLocaleString("fa-IR")} اتاق
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex w-full items-center justify-between overflow-hidden rounded-lg border border-[#e5e7eb] bg-white md:w-auto md:justify-center">
              <button
                type="button"
                onClick={() =>
                  isSelected
                    ? onChange(Math.max(0, selectedCount - 1))
                    : setQuantity((value) => Math.max(1, value - 1))
                }
                disabled={!isSelected && quantity <= 1}
                aria-label="کاهش تعداد اتاق"
                className="grid h-10 w-10 place-items-center text-[#1a1a2e] transition hover:bg-[#f5f6f8] disabled:text-[#cbd5e1]"
              >
                <PhosphorIcon name="Minus" size={14} />
              </button>
              <span className="min-w-14 border-x border-[#e5e7eb] px-3 text-center text-sm font-extrabold">
                {activeQuantity.toLocaleString("fa-IR")}
              </span>
              <button
                type="button"
                onClick={() =>
                  isSelected
                    ? onChange(Math.min(maxQuantity, selectedCount + 1))
                    : setQuantity((value) => Math.min(maxQuantity, value + 1))
                }
                disabled={activeQuantity >= maxQuantity}
                aria-label="افزایش تعداد اتاق"
                className="grid h-10 w-10 place-items-center text-[#1a1a2e] transition hover:bg-[#f5f6f8] disabled:text-[#cbd5e1]"
              >
                <PhosphorIcon name="Plus" size={14} />
              </button>
            </div>
            {isSelected ? (
              <span className="flex h-10 w-full min-w-0 items-center justify-center rounded-lg border border-primary/15 bg-primary/5 px-4 text-sm font-bold text-primary md:h-11 md:min-w-36 md:px-6">
                انتخاب شده
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onChange(quantity)}
                disabled={room.inventory < 1 || remainingSelectable <= 0}
                className="h-10 w-full min-w-0 rounded-lg bg-primary px-4 text-sm font-bold text-white transition hover:opacity-90 disabled:bg-slate-200 disabled:text-slate-500 md:h-11 md:min-w-36 md:px-6"
                data-analytics-event="hotel_room_selected"
                data-analytics-id={`hotel-detail-v2-room-${room.source.id}-${room.source.rate_plans.id}`}
                data-analytics-name={room.name}
                data-analytics-type="button"
                data-analytics-funnel="hotel_booking"
                data-analytics-step="detail_room_select"
                data-analytics-property-room-id={room.source.id}
                data-analytics-property-rate-plan-id={room.source.rate_plans.id}
                data-analytics-property-room-name={room.name}
                data-analytics-property-rate-plan-name={room.ratePlanName}
                data-analytics-property-quantity={String(quantity)}
                data-analytics-property-price={String(displayTotal)}
                data-analytics-property-nights={String(nights)}
                data-analytics-property-inventory={String(room.inventory)}
              >
                رزرو {quantity.toLocaleString("fa-IR")} اتاق
              </button>
            )}
            {/* TODO: Restore the room price calendar after its real data source is ready. */}
          </div>
        </div>
      </div>
      {/* TODO: Restore <RoomPriceCalendarDialog /> with the price calendar trigger. */}
    </article>
  );
}

function createRoomBadges(room: HotelDetailV2RoomModel): Array<{
  tone: BadgeTone;
  icon: PhosphorIconName;
  text: string;
}> {
  const badges: Array<{
    tone: BadgeTone;
    icon: PhosphorIconName;
    text: string;
  }> = [];
  if (room.breakfastIncluded && room.mealLabel) {
    badges.push({
      tone: "neutral",
      icon: "Coffee",
      text: room.mealLabel || "صبحانه",
    });
  }

  if (room.cancelable === false) {
    badges.push({
      tone: "neutral",
      icon: "XCircle",
      text: "غیرقابل استرداد",
    });
  }

  if (room.remaining > 0 && room.remaining <= 4) {
    badges.push({
      tone: "neutral",
      icon: "WarningCircle",
      text: `${room.remaining.toLocaleString("fa-IR")} اتاق باقی مانده`,
    });
  }

  if (room.source.rate_plans.reservation_state === "instant") {
    badges.push({
      tone: "neutral",
      icon: "Lightning",
      text: "رزرو آنی",
    });
  }

  const infantRule = room.source.rate_plans.rules?.child_rule;
  if (infantRule?.infant_count > 0) {
    badges.push({
      tone: "neutral",
      icon: "Baby",
      text: infantRule.max_infant_age
        ? `رایگان برای نوزاد تا ${infantRule.max_infant_age.toLocaleString("fa-IR")} سال`
        : "رایگان برای نوزاد",
    });
  }

  return badges;
}

function Badge({
  icon,
  text,
  tone,
}: {
  icon: PhosphorIconName;
  text: string;
  tone: BadgeTone;
}) {
  const toneClassNames: Record<BadgeTone, string> = {
    neutral: "border border-[#e5e7eb] bg-[#f8fafc] text-[#64748b]",
    discount: "bg-[#e6f7f1] text-emerald-700",
  };

  return (
    <span
      className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
        toneClassNames[tone]
      }`}
    >
      <PhosphorIcon name={icon} size={11} />
      {text}
    </span>
  );
}

type BadgeTone = "neutral" | "discount";
